import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { AudioVideoIcon, CCTVIcon, AutomationIcon, SoftwareIcon, CheckIcon } from '../components/Icons';
import { getHeroes, getServices } from '../services/api';
import './Services.css';

const Services = () => {
  const [hero, setHero] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heroData, servicesData] = await Promise.all([
          getHeroes(),
          getServices()
        ]);
        setHero(heroData.services);
        setServices(servicesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const serviceImages = {
    'audio-video': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
    'cctv': 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=800',
    'automation': 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800',
    'software': 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800'
  };

  const serviceIcons = {
    'audio-video': AudioVideoIcon,
    'cctv': CCTVIcon,
    'automation': AutomationIcon,
    'software': SoftwareIcon
  };

  return (
    <div className="services-page">
      <Hero
        title="Our Services"
        subtitle="Comprehensive technology solutions for homes and businesses"
        backgroundImage={hero?.image_url}
        ctaText="Get Quote"
        ctaLink="/contact"
        height="medium"
        overlay={0.7}
      />

      <section className="section">
        <div className="container">
          <div className="services-list">
            {(services.length > 0 ? services : defaultServices).map((service, index) => {
              const IconComponent = serviceIcons[service.service_key] || AudioVideoIcon;
              return (
                <div className={`service-row ${index % 2 !== 0 ? 'reverse' : ''}`} key={service.service_key}>
                  <div 
                    className="service-image"
                    style={{ backgroundImage: `url(${service.image_url || serviceImages[service.service_key]})` }}
                  >
                    <div className="service-image-overlay">
                      <IconComponent size={80} color="#FFFFFF" />
                    </div>
                  </div>
                  <div className="service-content">
                    <div className="service-header">
                      <IconComponent size={48} color="#FF5722" />
                      <h2>{service.title}</h2>
                    </div>
                    <p>{service.short_description}</p>
                    {service.features && service.features.length > 0 && (
                      <ul className="service-features">
                        {service.features.map((feature, i) => (
                          <li key={i}>
                            <CheckIcon size={20} color="#FF5722" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link to={`/services/${service.service_key}`} className="btn btn-primary">
                      Learn More
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Need a Custom Solution?</h2>
          <p>We'll work with you to design the perfect system for your needs</p>
          <Link to="/contact" className="btn btn-primary">Get Free Consultation</Link>
        </div>
      </section>
    </div>
  );
};

const defaultServices = [
  { 
    service_key: 'audio-video', 
    title: 'Audio/Video Systems', 
    short_description: 'Professional audio and video installation for homes and businesses. From home theaters to conference rooms, we deliver immersive experiences.',
    features: ['Home Theater Design', 'Multi-Room Audio', 'Conference Room AV', 'Outdoor Entertainment', 'TV Mounting']
  },
  { 
    service_key: 'cctv', 
    title: 'CCTV & Security', 
    short_description: 'Comprehensive security camera systems to protect your property. 24/7 monitoring and remote access from anywhere.',
    features: ['HD/4K Camera Systems', 'Remote Viewing Apps', 'Night Vision', 'Motion Detection', 'Cloud Storage']
  },
  { 
    service_key: 'automation', 
    title: 'Smart Automation', 
    short_description: 'Transform your space with intelligent automation. Control lighting, climate, shades, and more from your phone or voice.',
    features: ['Smart Lighting Control', 'Climate Automation', 'Motorized Shades', 'Voice Control', 'Energy Management']
  },
  { 
    service_key: 'software', 
    title: 'Software Development', 
    short_description: 'Custom software solutions including websites, mobile apps, and business systems tailored to your specific needs.',
    features: ['Custom Websites', 'Mobile Applications', 'CMS Solutions', 'E-Commerce', 'API Development']
  }
];

export default Services;
